/**
 * SpaLink — a plain `<a>` wrapper that uses `pushState` for v2-internal
 * navigation so clicking doesn't reload the page. Falls back to default
 * browser behavior for modifier-clicks (Cmd/Ctrl/Shift + click, middle
 * click, etc.), absolute URLs, and `target` other than `_self`.
 *
 *   <SpaLink href={`/b/${id}/manage`}>Manage</SpaLink>
 *
 * Mirrors the `<a>` API otherwise; pass `className`, `children`, etc.
 * Use this for any anchor that points at another v2 page. Leave external
 * links (Docs, Privacy, Terms outbound, anchors with `target="_blank"`)
 * as raw `<a>` so users get expected browser behavior.
 *
 * The ref is forwarded to the underlying `<a>` — Radix primitives that take a
 * `SpaLink` via `asChild` (e.g. a `DropdownMenuItem`) need it for their focus
 * management, and throw on a null ref.
 */
import React from 'react';
import { navigate } from './useUrl';

type Props = Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & {
  href: string;
  replace?: boolean;
};

function isModifiedClick(e: React.MouseEvent): boolean {
  return e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0;
}

function isExternal(href: string): boolean {
  // Anything starting with a scheme or `//` is external — let the browser handle it.
  return /^[a-z][a-z0-9+.-]*:/i.test(href) || href.startsWith('//');
}

const SpaLink = React.forwardRef<HTMLAnchorElement, Props>(function SpaLink(
  { href, replace, onClick, target, children, ...rest }, ref,
) {
  const handle = (e: React.MouseEvent<HTMLAnchorElement>) => {
    onClick?.(e);
    if (e.defaultPrevented) return;
    if (target && target !== '_self') return;
    if (isModifiedClick(e)) return;
    if (isExternal(href)) return;
    e.preventDefault();
    navigate(href, { replace });
  };
  return (
    <a ref={ref} href={href} target={target} onClick={handle} {...rest}>
      {children}
    </a>
  );
});

export default SpaLink;
